import { defineStore } from "pinia";
import piniaPersistedState from "pinia-plugin-persistedstate";

export const useRosterStore = defineStore("roster", {
    state: () => ({
        // Define your initial state
        characters: [],
        abilities: [],
        archetypes: [],
        armour: [],
        armourAbilities: [],
        backgrounds: [],
        backgroundAbilities: [],
        bestiary: [],
        bestiaryAbilities: [],
        bestiaryAttacks: [],
        encounters: [],
        flaws: [],
        items: [],
        itemAbilities: [],
        fatalFlaws: [],
        mutagens: [],
        weapons: [],
        weaponAbilities: [],
        theme: 1,
        stories: [],
        monsterTypes: {},
        statusEffects: [],
    }),
    actions: {
        // Action to add a new character
        addCharacter(character) {
            const maxId = this.characters.reduce(
                (max, c) => Math.max(max, c.id || 0),
                0
            );
            character.id = maxId + 1;

            // Defaults
            character.lifeline = character.lifeline ?? 0;
            character.doomclock = character.doomclock ?? 0;
            character.speed = character.speed || "Average - 30ft / 6 Hexes";
            character.fearStatus = character.fearStatus || "Stable";
            character.mutagens = Array.isArray(character.mutagens)
                ? character.mutagens
                : [];

            this.characters.push(character);
        },
        addEncounter(storyId, encounter) {
            const story = this.getStoryById(storyId);
            if (!story) alert("Story not found for ID: " + storyId);
            story.encounters.push(encounter);
        },

        updateEncounter(storyId, updated) {
            const story = this.getStoryById(storyId);
            if (!story) return;
            const i = story.encounters.findIndex((e) => e.id === updated.id);
            if (i !== -1) story.encounters[i] = updated;
        },

        addStory(story) {
            const maxId = this.stories.reduce(
                (max, c) => Math.max(max, c.id || 0),
                0
            );
            story.id = maxId + 1;

            // 🔑 Deep clone monsters so they don’t share references
            if (story.monsters) {
                story.monsters = story.monsters.map((m) =>
                    JSON.parse(JSON.stringify(m))
                );
            }

            this.stories.push(story);
        },
        // Action to delete all characters
        deleteAllCharacters() {
            this.characters = [];
        },

        // Action to remove a character by index
        removeCharacter(characterId) {
            this.characters = this.characters.filter(
                (c) => c.id !== characterId
            );
        },
        // Action to remove an encounter by index
        removeEncounter(id) {
            this.encounters = this.encounters.filter((c) => c.id !== id);
        },
        // Action to remove a story by index
        removeStory(id) {
            this.stories = this.stories.filter((c) => c.id !== id);
        },
        // Action to update a character by index
        updateCharacter(index, updatedCharacter) {
            this.characters[index] = updatedCharacter;
        },
        updateStories(index, updatedStory) {
            this.stories[index] = updatedStory;
        },
        updateStoryDoomclock(storyId, newDoomclock) {
            const index = this.stories.findIndex((s) => s.id === storyId);
            if (index !== -1) {
                this.stories[index].doomclock = newDoomclock;
            }
        },
        updateMonsterInEncounter(encounterId, monsterIndex, updatedMonster) {
            const encounter = this.encounters.find((e) => e.id === encounterId);
            if (!encounter) {
                console.warn("Encounter not found:", encounterId);
                return;
            }

            // Replace monster at index
            if (encounter.monsters && encounter.monsters[monsterIndex]) {
                encounter.monsters[monsterIndex] = JSON.parse(
                    JSON.stringify(updatedMonster)
                );
            } else {
                console.warn(
                    "Monster not found at index",
                    monsterIndex,
                    "in encounter",
                    encounterId
                );
            }
        },
        removeMonsterByName(encounterId, name) {
            const enc = this.encounters.find((e) => e.id === encounterId);
            if (!enc) return;
            enc.monsters = enc.monsters.filter((m) => m.name !== name);
        },

        moveMonsterInEncounter(encounterId, fromIndex, toIndex) {
            const enc = this.encounters.find((e) => e.id === encounterId);
            if (!enc) return;
            if (toIndex < 0 || toIndex >= enc.monsters.length) return;

            const [m] = enc.monsters.splice(fromIndex, 1);
            enc.monsters.splice(toIndex, 0, m);
        },

        moveEncounterInStory(storyId, fromIndex, toIndex) {
            const story = this.getStoryById(storyId);
            if (!story || !story.encounters) return;
            if (toIndex < 0 || toIndex >= story.encounters.length) return;

            const [moved] = story.encounters.splice(fromIndex, 1);
            story.encounters.splice(toIndex, 0, moved);
        },

        // -------------------
        // NEW: compendium actions
        // -------------------

        /** Overwrite all compendium data */
        overwriteCompendium(compendium) {
            this.archetypes = compendium.archetypes || [];
            this.armour = compendium.armour || [];
            this.armourAbilities = compendium.armourAbilities || [];
            this.abilities = compendium.abilities || [];
            this.items = compendium.items || [];
            this.itemAbilities = compendium.itemAbilities || [];
            this.bestiary = compendium.bestiary || [];
            this.bestiaryAbilities = compendium.bestiaryAbilities || [];
            this.bestiaryAttacks = compendium.bestiaryAttacks || [];
            this.flaws = compendium.flaws || [];
            this.fatalFlaws = compendium.fatalFlaws || [];
            this.backgroundAbilities = compendium.backgroundAbilities || [];
            this.backgrounds = compendium.backgrounds || [];
            this.weapons = compendium.weapons || [];
            this.weaponAbilities = compendium.weaponAbilities || [];
            this.mutagens = compendium.mutagens || [];
            this.monsterTypes = compendium.monsterTypes || [];
            this.statusEffects = compendium.statusEffects || [];
        },

        appendToCompendium(newData) {
            if (newData.archetypes) {
                this.archetypes.push(...newData.archetypes);
            }
            if (newData.abilities) {
                this.abilities.push(...newData.abilities);
            }
            if (newData.armour) {
                this.armour.push(...newData.armour);
            }
            if (newData.armourAbilities) {
                this.armourAbilities.push(...newData.armourAbilities);
            }
            if (newData.flaws) {
                this.flaws.push(...newData.flaws);
            }
            if (newData.fatalFlaws) {
                this.fatalFlaws.push(...newData.fatalFlaws);
            }
            if (newData.backgroundAbilities) {
                this.backgroundAbilities.push(...newData.backgroundAbilities);
            }
            if (newData.backgrounds) {
                this.backgrounds.push(...newData.backgrounds);
            }
            if (newData.weapons) {
                this.weapons.push(...newData.weapons);
            }
            if (newData.weaponAbilities) {
                this.weaponAbilities.push(...newData.weaponAbilities);
            }
            if (newData.mutagens) {
                this.mutagens.push(...newData.mutagens);
            }
            if (newData.items) {
                this.items.push(...newData.items);
            }
            if (newData.itemAbilities) {
                this.itemAbilities.push(...newData.itemAbilities);
            }
            if (newData.bestiary) {
                this.bestiary.push(...newData.bestiary);
            }
            if (newData.bestiaryAbilities) {
                this.bestiaryAbilities.push(...newData.bestiaryAbilities);
            }
            if (newData.bestiaryAttacks) {
                this.bestiaryAttacks.push(...newData.bestiaryAttacks);
            }
            if (newData.monsterTypes) {
                this.monsterTypes.push(...newData.monsterTypes);
            }
            if (newData.statusEffects) {
                this.statusEffects.push(...newData.statusEffects);
            }
        },

        /** Export everything as a single JSON string */
        exportCompendium() {
            const compendium = {
                abilities: this.abilities,
                archetypes: this.archetypes,
                armour: this.armour,
                armourAbilities: this.armourAbilities,
                backgrounds: this.backgrounds,
                backgroundAbilities: this.backgroundAbilities,
                bestiary: this.bestiary,
                bestiaryAbilities: this.bestiaryAbilities,
                bestiaryAttacks: this.bestiaryAttacks,
                flaws: this.flaws,
                fatalFlaws: this.fatalFlaws,
                items: this.items,
                itemAbilities: this.itemAbilities,
                monsterTypes: this.monsterTypes,
                mutagens: this.mutagens,
                statusEffects: this.statusEffects,
                weapons: this.weapons,
                weaponAbilities: this.weaponAbilities,
            };
            return JSON.stringify(compendium, null, 2);
        },
    },
    getters: {
        getCharacterById: (state) => (id) => {
            const numericId = Number(id); // Convert id to a number

            const foundCharacter = state.characters.find((character) => {
                // Persisted/imported data may contain IDs as strings.
                return Number(character.id) === numericId;
            });

            if (!foundCharacter) {
                alert("Character not found for ID:", id); // Log if not found
            }

            return foundCharacter;
        },
        getEncounterById: (state) => (id) => {
            const numericId = Number(id);
            const foundEncounter = state.encounters.find(
                (encounter) => encounter.id === numericId
            );

            if (!foundEncounter) {
                alert("Encounter not found for ID: " + id);
                return null;
            }

            // 🔑 return a clone so callers can’t mutate store directly
            return JSON.parse(JSON.stringify(foundEncounter));
        },
        getStoryById: (state) => (id) => {
            const numericId = Number(id);
            const foundStory = state.stories.find(
                (story) => story.id === numericId
            );

            if (!foundStory) {
                alert("Story not found for ID: " + id);
                return null;
            }

            // 🔑 return a clone so callers can’t mutate store directly
            return foundStory;
        },
    },
    persist: {
        enabled: true,
    },
});

